import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import {
  FileText,
  Share2,
  MessageSquare,
  Check,
  Clock,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Eye,
  AlertCircle,
  CheckCircle,
  ThumbsUp,
  ThumbsDown,
  ClipboardList,
  Loader2,
  Cloud,
  TrendingUp,
  RefreshCw,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, FadeIn } from 'react-native-reanimated';
import { DataRoomDocument } from '@/lib/state/inverio-store';
import { useScoreStore } from '@/lib/state/score-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useScoreHistoryStore } from '@/lib/state/score-history-store';
import { useTrackScreen } from '@/lib/analytics';
import { useThemeColors } from '@/lib/useThemeColors';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout } from '@/components/AppLayout';
import { ScreenFrame } from '@/components/ScreenFrame';
import { useAlert } from '@/components/AlertDialog';
import { SectionDivider } from '@/components/SectionDivider';
import AILauncherFAB from '@/components/AILauncherFAB';
import {
  AccessRequestsSection,
  type AccessRequest,
  DocumentTypeChip,
  StaleCommentTag,
} from '@/components/dataroom';
import { mode } from '@/lib/config/app-mode';
import { getMockAccessRequests } from '@/lib/state/mock-data-provider';
import { useDataRoomAccessRequests, useRespondToAccessRequest } from '@/lib/supabase';
import { mapAccessRequestRow } from '@/lib/dataroom/access-requests';
import { syncFromProvider, type ProviderFile } from '@/lib/dataroom/sync';
import { classifyAmbiguousByLLM } from '@/lib/dataroom/llm-classify';
import { timeAgo } from '@/lib/dataroom/activity';
import { useDataRoomViewStats } from '@/lib/dataroom/use-dataroom-views';
import { resolveSyncFolderId, resolveSyncOutcome } from '@/lib/dataroom/sync-helpers';
import { useCloudReauth } from '@/lib/dataroom/use-cloud-reauth';
import { listDropboxFolder } from '@/lib/dropbox';
import { useDataroomStore } from '@/lib/state/dataroom-store';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  useDataRoomStore,
  calculateProgress,
  isDocumentRequired,
  isDocumentApplicable,
  DATA_ROOM_DOCUMENTS,
} from '@/lib/dataroom';
import { reportError, handleError } from '@/lib/errors';
import { useAiConsentGate } from '@/lib/consent/ai-consent';

// Standard shadow styles
const cardShadow = {
  shadowColor: '#000',
  shadowOpacity: 0.05,
  shadowRadius: 8,
  shadowOffset: { width: 0, height: 2 },
  elevation: 2,
};

const getDocumentTypeLabels = (colors: ReturnType<typeof useThemeColors>): Record<string, { label: string; color: string }> => ({
  pitch_deck: { label: 'Pitch Deck', color: colors.primary[400] },
  financials: { label: 'Financials', color: colors.success },
  legal: { label: 'Legal', color: colors.warning },
  product: { label: 'Product', color: colors.info },
  team: { label: 'Team', color: colors.secondary[400] },
  market_research: { label: 'Market Research', color: colors.primary[300] },
  other: { label: 'Other', color: colors.dark[400] },
});

const getStatusConfig = (colors: ReturnType<typeof useThemeColors>): Record<string, { icon: typeof Check; color: string; bg: string }> => ({
  pending: { icon: Clock, color: colors.warning, bg: `${colors.warning}20` },
  reviewed: { icon: Check, color: colors.success, bg: `${colors.success}20` },
  flagged: { icon: AlertTriangle, color: colors.error, bg: `${colors.error}20` },
});

const getDecisionConfig = (colors: ReturnType<typeof useThemeColors>) => ({
  invest: { color: colors.success, icon: ThumbsUp, label: 'Invest' },
  pass: { color: colors.error, icon: ThumbsDown, label: 'Pass' },
  follow_up: { color: colors.warning, icon: Clock, label: 'Follow Up' },
});

const BREADCRUMBS = [
  { label: 'Dashboard', href: '/(startup)/startup-dashboard' },
  { label: 'Data Room' },
];

export default function DataRoomScreen() {
  useTrackScreen('DataRoom');
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const documentTypeLabels = getDocumentTypeLabels(colors);
  const statusConfig = getStatusConfig(colors);
  // Same as the investor DD screen: `data_room_documents.status` is a plain TEXT
  // column, so an unmapped value here yields `undefined` and `statusInfo.icon`
  // throws. The union type is an assumption about the row, not a constraint on it.
  const statusFallback = { icon: Clock, color: colors.text.tertiary, bg: colors.light[300] };
  const decisionConfig = getDecisionConfig(colors);
  const documents = useDataroomStore((s) => s.dataRoomDocuments);
  const startupName = useProfileStore((s) => s.startupProfile?.name) ?? 'Your Startup';
  const startupId = useProfileStore((s) => s.startupProfile?.id) ?? '';
  const triggerAIScoring = useScoreStore((s) => s.triggerAIScoring);
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const { runWithConsent: runScoringWithConsent, sheet: scoringConsentSheet } = useAiConsentGate(['ai_scoring']);
  const [selectedDoc, setSelectedDoc] = useState<DataRoomDocument | null>(null);
  const { alert, confirm, error: showError } = useAlert();

  // Access requests — REAL in production (data_room_access_requests via React
  // Query; approve/reject goes through respond_to_data_room_request, which
  // writes the public.data_room_access grant AND moves the deal's stage to
  // `diligence`. It used to flip a dealflow.data_room_access boolean too — that
  // second store was retired in 20260805160000). Sample data only under
  // showSampleData, mutated locally (not persisted).
  const { data: realRequestRows = [] } = useDataRoomAccessRequests(
    mode.showSampleData ? undefined : startupId,
  );
  const respondToAccessRequest = useRespondToAccessRequest();
  const [mockRequests, setMockRequests] = useState<AccessRequest[]>(() =>
    mode.showSampleData
      ? getMockAccessRequests().filter((r) => r.startupId === startupId)
      : [],
  );
  const accessRequests: AccessRequest[] = mode.showSampleData
    ? mockRequests
    : realRequestRows.map(mapAccessRequestRow);
  const updateRequestStatus = useCallback(
    (id: string, status: AccessRequest['status']) => {
      if (status === 'pending') return; // only approve / reject (revoke) are actions
      if (mode.showSampleData) {
        setMockRequests((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status, respondedAt: new Date().toISOString() } : r)),
        );
        return;
      }
      // Guard against a double-grant: ignore taps while a response is in flight.
      if (respondToAccessRequest.isPending) return;
      // Surface failures honestly — a silent fire-and-forget left the founder
      // believing a grant/deny persisted when it may have failed.
      respondToAccessRequest.mutate(
        { requestId: id, startupId, status },
        {
          onError: (err) => {
            const { message, isCancelled } = handleError(err, {
              context: 'Dataroom.updateRequestStatus',
            });
            if (!isCancelled) showError('Could Not Update Request', message);
          },
        },
      );
    },
    [respondToAccessRequest, startupId, showError],
  );

  // Score history store
  const isScoring = useScoreHistoryStore((s) => s.isScoring);

  // Checklist store — used to auto-mark matched checklist items as 'uploaded' on sync.
  const checklistUploads = useDataRoomStore((s) => s.uploads);
  const setChecklistDocStatus = useDataRoomStore((s) => s.setDocumentStatus);
  const checklistStage = useDataRoomStore((s) => s.currentStage);

  // DD checklist progress — derived from the checklist store. Drives the new
  // Overview stats so the page shows real investor-readiness metrics.
  const checklistProgress = useMemo(
    () => calculateProgress(checklistUploads, checklistStage),
    [checklistUploads, checklistStage],
  );
  const requiredMissingCount = useMemo(
    () =>
      DATA_ROOM_DOCUMENTS.filter((doc) => {
        if (!isDocumentApplicable(doc.requirements[checklistStage])) return false;
        if (!isDocumentRequired(doc.requirements[checklistStage])) return false;
        const status = checklistUploads[doc.id]?.status || 'not-started';
        return status === 'not-started';
      }).length,
    [checklistUploads, checklistStage],
  );
  const setDataRoomDocuments = useDataroomStore((s) => s.setDataRoomDocuments);
  const setDocumentType = useDataroomStore((s) => s.setDocumentType);
  const dataRoomProvider = useProfileStore((s) => s.startupProfile?.dataRoomProvider);
  const dataRoomFolderId = useProfileStore((s) => s.startupProfile?.dataRoomFolderId);
  // Real per-document view telemetry (data_room_events) — distinct viewers +
  // last-viewed via the get_data_room_view_stats() RPC. Absent from the map ⇒
  // no recorded views ⇒ show nothing (never a fabricated count).
  const { data: viewStats } = useDataRoomViewStats(startupId);

  // Last cloud sync timestamp — persisted (survives reload, cleared on sign-out).
  const lastSyncedAt = useDataRoomStore((s) => s.lastSyncedAt);
  const setLastSyncedAt = useDataRoomStore((s) => s.setLastSyncedAt);

  // Reconnect (in-place re-auth) state.
  const [reconnectNeeded, setReconnectNeeded] = useState(false);
  const { reconnect, reconnecting, justReconnected, clearJustReconnected } = useCloudReauth();

  // Documents section collapsed state (default expanded — it's the page's main content)
  const [documentsExpanded, setDocumentsExpanded] = useState(true);

  // Self-seed mock documents so the page works when entered directly in mock mode.
  useEffect(() => {
    if (!mode.showSampleData) return;
    if (documents.length > 0) return;
    const { getMockDataRoomDocuments } = require('@/lib/state/mock-data-provider');
    const mock = getMockDataRoomDocuments();
    if (mock?.length) setDataRoomDocuments(mock);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Sync action — fetches the founder's selected cloud folder (Google or
  // Dropbox), then classifies. In production a failed/expired token surfaces a
  // reconnect prompt — it NEVER silently substitutes mock documents. Mock/demo
  // mode keeps sample data so the flow stays testable (resolveSyncOutcome).
  const handleSyncDrive = useCallback(async () => {
    if (!dataRoomProvider || dataRoomProvider === 'docsend') return;

    const folderId = resolveSyncFolderId(dataRoomProvider, dataRoomFolderId);
    const providerLabel = dataRoomProvider === 'google' ? 'Google Drive' : 'Dropbox';
    let providerFiles: ProviderFile[] = [];
    let authFailed = false;

    try {
      if (dataRoomProvider === 'google') {
        const { listFiles } = await import('@/lib/google-drive/api');
        const { files } = await listFiles(folderId);
        providerFiles = files.map((f) => ({
          id: f.id,
          name: f.name,
          size: f.size,
          modifiedAt: f.modifiedTime,
          createdAt: f.modifiedTime,
        }));
      } else if (dataRoomProvider === 'dropbox') {
        const { files } = await listDropboxFolder(folderId);
        providerFiles = files.map((f) => ({
          id: f.id,
          name: f.name,
          size: f.size != null ? String(f.size) : undefined,
          folderPath: f.pathDisplay,
          modifiedAt: f.serverModified,
          createdAt: f.clientModified ?? f.serverModified,
        }));
      }
    } catch (err) {
      // Token expired / not connected / network. Do NOT fall back to mock in
      // production — resolveSyncOutcome turns this into a reconnect prompt.
      authFailed = true;
      reportError(err, { context: 'dataroom.handleSyncDrive', level: 'warning', silent: true });
    }

    const outcome = resolveSyncOutcome({
      fileCount: providerFiles.length,
      authFailed,
      showSampleData: mode.showSampleData,
    });

    if (outcome.kind === 'reconnect') {
      setReconnectNeeded(true);
      alert(
        'Reconnect needed',
        `Your ${providerLabel} session has expired. Tap Reconnect to restore access, then sync again.`,
      );
      return;
    }

    if (outcome.kind === 'empty') {
      setLastSyncedAt(new Date().toISOString());
      alert('No documents found', `We connected to ${providerLabel}, but the selected folder is empty.`);
      return;
    }

    if (outcome.kind === 'mock') {
      providerFiles = [
        { id: 'gd-1', name: 'Pitch_Deck_v3.pdf', size: '2.4 MB', folderPath: 'Investors/Seed', modifiedAt: new Date().toISOString(), createdAt: '2026-01-12T10:00:00Z' },
        { id: 'gd-2', name: 'Q1_Financial_Model.xlsx', size: '180 KB', folderPath: 'Financials', modifiedAt: '2026-04-01T09:00:00Z' },
        { id: 'gd-3', name: 'Cap_Table_2026.xlsx', size: '52 KB', folderPath: 'Legal', modifiedAt: '2026-03-22T15:00:00Z' },
        { id: 'gd-4', name: 'Team_Bios.pdf', size: '380 KB', folderPath: 'Team', modifiedAt: '2026-02-04T11:00:00Z' },
        { id: 'gd-5', name: 'misc_notes_2026.docx', size: '24 KB', folderPath: 'Misc', modifiedAt: '2026-04-20T11:00:00Z' },
      ];
    }

    // outcome.kind is 'files' or 'mock' from here — clear any stale reconnect flag.
    setReconnectNeeded(false);

    // Pass 1 — heuristic classifier inside syncFromProvider
    const result = syncFromProvider(documents, providerFiles);

    // Pass 2 — LLM fallback for documents that ended up as 'other' with low confidence.
    const ambiguous = result.documents.filter(
      (d) => d.type === 'other' && d.classificationConfidence === 'low' && !d.typeOverridden,
    );
    let llmFixed = 0;
    let finalDocs = result.documents;
    if (ambiguous.length > 0) {
      const llm = await classifyAmbiguousByLLM(
        ambiguous.map((d) => ({ id: d.id, name: d.name })),
      );
      finalDocs = result.documents.map((d) => {
        const next = llm.classifications.get(d.id);
        if (next && next !== d.type) {
          llmFixed += 1;
          return { ...d, type: next, classificationConfidence: 'medium' as const };
        }
        return d;
      });
    }
    setDataRoomDocuments(finalDocs);

    // Pass 3 — auto-mark matched checklist items as 'uploaded' so the checklist
    // stays in sync with the connected drive.
    let checklistAutoChecked = 0;
    for (const d of finalDocs) {
      if (!d.linkedChecklistDocId) continue;
      const current = checklistUploads[d.linkedChecklistDocId]?.status;
      if (current === 'uploaded' || current === 'verified') continue;
      setChecklistDocStatus(d.linkedChecklistDocId, 'uploaded', {
        fileName: d.name,
        notes: `Auto-detected from ${dataRoomProvider === 'google' ? 'Google Drive' : dataRoomProvider === 'dropbox' ? 'Dropbox' : 'DocSend'}`,
      });
      checklistAutoChecked += 1;
    }

    setLastSyncedAt(new Date().toISOString());
    const llmHint = llmFixed > 0 ? ` · ${llmFixed} AI-classified` : '';
    const checklistHint = checklistAutoChecked > 0 ? ` · ${checklistAutoChecked} checklist item${checklistAutoChecked === 1 ? '' : 's'} auto-checked` : '';
    alert(
      'Drive synced',
      `${result.added} new · ${result.versionBumped} updated · ${result.unchanged} unchanged${llmHint}${checklistHint}`,
    );

    // Trigger AI re-scoring when the document set actually changed. Fire-and-forget;
    // the Score Hero card / score history will pick up the new value.
    if (result.added > 0 || result.versionBumped > 0) {
      runScoringWithConsent(() => {
        triggerAIScoring({ type: 'document_added' }, startupProfile).catch((err) => {
          reportError(err, { context: 'Dataroom.handleSyncDrive.triggerAIScoring', level: 'warning', silent: true });
        });
      });
    }
  }, [dataRoomProvider, dataRoomFolderId, documents, setDataRoomDocuments, setLastSyncedAt, alert, checklistUploads, setChecklistDocStatus, triggerAIScoring, startupProfile, runScoringWithConsent]);

  // After a successful in-place reconnect, clear the flag and auto-sync so the
  // founder doesn't have to tap Sync a second time.
  useEffect(() => {
    if (!justReconnected) return;
    setReconnectNeeded(false);
    clearJustReconnected();
    handleSyncDrive();
  }, [justReconnected, clearJustReconnected, handleSyncDrive]);

  const stats = {
    total: documents.length,
  };

  const handleShare = async (doc: DataRoomDocument) => {
    const confirmed = await confirm(
      'Share Document',
      `Share "${doc.name}" with investors? They will be able to view and comment on this document.`
    );
    if (confirmed) {
      // Handle share action
    }
  };

  return (
    <AppLayout title="Data Room" breadcrumbs={BREADCRUMBS}>
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* Pinned, and carrying the global controls — see the note on score.tsx.
           Connect stays this page's contextual action. No bottomEdge: tab screen. */}
        <ScreenFrame
          eyebrow="Data Room"
          title={startupName}
          actions={
            <>
              {!dataRoomProvider && (
                <AnimatedPressable
                  onPress={() => router.push('/(onboarding)/data-room-connect')}
                  className="rounded-full px-4 flex-row items-center justify-center gap-2"
                  style={{ height: 44, backgroundColor: colors.primary[600] }}
                  accessibilityRole="button"
                  accessibilityLabel="Connect data room"
                >
                  <Cloud size={18} color="white" />
                  <Text className="text-white text-sm font-bold">Connect</Text>
                </AnimatedPressable>
              )}
              <ScreenFrame.GlobalActions />
            </>
          }
        >
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Active scoring indicator only — idle "last scored" timestamp is redundant
                with the Overview "Last sync" stat below. Descriptive, so it scrolls;
                the page's identity and controls stay pinned above. */}
            {phoneShell && isScoring && (
              <Animated.View
                entering={FadeInDown.delay(100).duration(500)}
                className="px-5 pb-4 flex-row items-center gap-2"
              >
                <Loader2 size={14} color={colors.primary[600]} />
                <Text style={{ color: colors.primary[600] }} className="text-xs font-medium" numberOfLines={1}>
                  Analyzing documents...
                </Text>
              </Animated.View>
            )}


          {/* Section Divider - Overview */}
          <View className="px-5">
            <SectionDivider label="Overview" marginTop={8} marginBottom={12} />
          </View>

          {/* Stats Grid */}
          <Animated.View
            entering={FadeInUp.delay(200).duration(500)}
            className="px-5 mb-6"
          >
            <View className="flex-row flex-wrap gap-3">
              {/* Documents — total file count from connected drive + manual */}
              <View
                className="rounded-2xl p-4"
                style={{ flexBasis: '48%', flexGrow: 1, backgroundColor: colors.surface.primary, ...cardShadow }}
              >
                <View
                  className="w-10 h-10 rounded-xl items-center justify-center mb-3"
                  style={{ backgroundColor: `${colors.primary[600]}15` }}
                >
                  <FileText size={20} color={colors.primary[600]} />
                </View>
                <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
                  {stats.total}
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">Documents</Text>
              </View>

              {/* Checklist Complete — primary investor-readiness signal. Tappable. */}
              <AnimatedPressable
                onPress={() => router.push('/(startup)/dataroom-checklist')}
                style={{
                  flexBasis: '48%',
                  flexGrow: 1,
                  backgroundColor: colors.surface.primary,
                  borderRadius: 16,
                  padding: 16,
                  ...cardShadow,
                }}
              >
                <View
                  className="w-10 h-10 rounded-xl items-center justify-center mb-3"
                  style={{
                    backgroundColor:
                      checklistProgress.completionPercentage >= 80
                        ? `${colors.success}15`
                        : checklistProgress.completionPercentage >= 50
                          ? `${colors.warning}15`
                          : `${colors.error}15`,
                  }}
                >
                  <TrendingUp
                    size={20}
                    color={
                      checklistProgress.completionPercentage >= 80
                        ? colors.success
                        : checklistProgress.completionPercentage >= 50
                          ? colors.warning
                          : colors.error
                    }
                  />
                </View>
                <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
                  {checklistProgress.completionPercentage}%
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  Checklist complete
                </Text>
              </AnimatedPressable>

              {/* Critical Missing — count of required docs still 'not-started'. Tappable. */}
              <AnimatedPressable
                onPress={() => router.push('/(startup)/dataroom-checklist')}
                style={{
                  flexBasis: '48%',
                  flexGrow: 1,
                  backgroundColor: requiredMissingCount > 0 ? colors.red.light : colors.surface.primary,
                  borderRadius: 16,
                  padding: 16,
                  ...cardShadow,
                }}
              >
                <View
                  className="w-10 h-10 rounded-xl items-center justify-center mb-3"
                  style={{
                    backgroundColor:
                      requiredMissingCount > 0 ? colors.surface.primary : `${colors.success}15`,
                  }}
                >
                  {requiredMissingCount > 0 ? (
                    <AlertCircle size={20} color={colors.error} />
                  ) : (
                    <CheckCircle size={20} color={colors.success} />
                  )}
                </View>
                <Text
                  className="text-2xl font-bold"
                  style={{ color: requiredMissingCount > 0 ? colors.error : colors.success }}
                >
                  {requiredMissingCount > 0 ? requiredMissingCount : 'None'}
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  Critical missing
                </Text>
              </AnimatedPressable>

              {/* Last Sync — operational pulse */}
              <View
                className="rounded-2xl p-4"
                style={{ flexBasis: '48%', flexGrow: 1, backgroundColor: colors.surface.primary, ...cardShadow }}
              >
                <View
                  className="w-10 h-10 rounded-xl items-center justify-center mb-3"
                  style={{ backgroundColor: `${colors.primary[600]}15` }}
                >
                  <RefreshCw size={20} color={colors.primary[600]} />
                </View>
                <Text
                  style={{ color: colors.text.primary }}
                  className="text-2xl font-bold"
                  numberOfLines={1}
                >
                  {lastSyncedAt ? (timeAgo(lastSyncedAt) ?? 'now') : '—'}
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">Last sync</Text>
              </View>
            </View>
          </Animated.View>

          {/* Connection Hero — primary CTA. Connect or Sync depending on state. */}
          <Animated.View
            entering={FadeInUp.delay(300).duration(500)}
            className="px-5 mb-4"
          >
            {dataRoomProvider ? (
              // Connected state — show provider + last sync + big Sync button
              <View
                className="rounded-2xl p-4"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  ...cardShadow,
                }}
              >
                <View className="flex-row items-center gap-3 mb-3">
                  <View
                    className="w-11 h-11 rounded-xl items-center justify-center"
                    style={{
                      backgroundColor:
                        dataRoomProvider === 'google'
                          ? '#E8F0FE'
                          : dataRoomProvider === 'dropbox'
                            ? '#E6F0FF'
                            : '#FFF1EC',
                    }}
                  >
                    <Cloud
                      size={22}
                      color={
                        dataRoomProvider === 'google'
                          ? '#4285F4'
                          : dataRoomProvider === 'dropbox'
                            ? '#0061FF'
                            : '#FF7A45'
                      }
                    />
                  </View>
                  <View className="flex-1">
                    <Text
                      style={{ color: colors.text.primary }}
                      className="font-semibold text-base"
                      numberOfLines={1}
                    >
                      {dataRoomProvider === 'google'
                        ? 'Google Drive connected'
                        : dataRoomProvider === 'dropbox'
                          ? 'Dropbox connected'
                          : 'DocSend connected'}
                    </Text>
                    <Text
                      style={{ color: reconnectNeeded ? colors.warning : colors.text.tertiary }}
                      className="text-xs"
                      numberOfLines={1}
                    >
                      {reconnectNeeded
                        ? 'Session expired — reconnect to sync'
                        : lastSyncedAt
                          ? `Last synced ${timeAgo(lastSyncedAt) ?? 'just now'}`
                          : 'Tap Sync to scan and classify your files'}
                    </Text>
                  </View>
                </View>
                {reconnectNeeded && dataRoomProvider !== 'docsend' ? (
                  <AnimatedPressable
                    onPress={() => reconnect(dataRoomProvider as 'google' | 'dropbox')}
                    disabled={reconnecting}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      paddingVertical: 10,
                      borderRadius: 10,
                      backgroundColor: colors.warning,
                      opacity: reconnecting ? 0.7 : 1,
                    }}
                  >
                    <RefreshCw size={16} color="#fff" />
                    <Text className="text-white font-semibold text-sm">
                      {reconnecting ? 'Reconnecting…' : 'Reconnect'}
                    </Text>
                  </AnimatedPressable>
                ) : (
                  <AnimatedPressable
                    onPress={handleSyncDrive}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      paddingVertical: 10,
                      borderRadius: 10,
                      backgroundColor: colors.primary[600],
                    }}
                  >
                    <RefreshCw size={16} color="#fff" />
                    <Text className="text-white font-semibold text-sm">Sync now</Text>
                  </AnimatedPressable>
                )}
              </View>
            ) : (
              // Not connected — primary CTA points to onboarding connect flow
              <AnimatedPressable
                onPress={() => router.push('/(onboarding)/data-room-connect')}
                className="rounded-2xl p-4"
                style={{
                  backgroundColor: colors.primary[50],
                  borderWidth: 1,
                  borderColor: colors.primary[200],
                  ...cardShadow,
                }}
              >
                <View className="flex-row items-center gap-3">
                  <View
                    className="w-11 h-11 rounded-xl items-center justify-center"
                    style={{ backgroundColor: '#fff' }}
                  >
                    <Cloud size={22} color={colors.primary[600]} />
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.primary }} className="font-semibold text-base">
                      Connect your data room
                    </Text>
                    <Text style={{ color: colors.text.secondary }} className="text-xs mt-0.5">
                      Link Google Drive, Dropbox, or DocSend to auto-classify your files against the DD checklist
                    </Text>
                  </View>
                  <ChevronRight size={20} color={colors.primary[600]} />
                </View>
              </AnimatedPressable>
            )}
          </Animated.View>

          {/* DD Checklist link — secondary action */}
          <Animated.View
            entering={FadeInUp.delay(350).duration(500)}
            className="px-5 mb-4"
          >
            <AnimatedPressable
              onPress={() => router.push('/(startup)/dataroom-checklist')}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                paddingHorizontal: 14,
                paddingVertical: 12,
                borderRadius: 12,
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <View
                className="w-8 h-8 rounded-lg items-center justify-center"
                style={{ backgroundColor: `${colors.secondary[500]}15` }}
              >
                <ClipboardList size={16} color={colors.secondary[500]} />
              </View>
              <View style={{ flex: 1 }}>
                <Text
                  style={{ color: colors.text.primary, fontSize: 13, fontWeight: '600' }}
                  numberOfLines={1}
                >
                  Due Diligence Checklist
                </Text>
                <Text
                  style={{ color: colors.text.tertiary, fontSize: 11, marginTop: 1 }}
                  numberOfLines={1}
                >
                  Review what investors expect at your stage
                </Text>
              </View>
              <ChevronRight size={16} color={colors.text.tertiary} />
            </AnimatedPressable>
          </Animated.View>

          {/* (Investor Feedback section removed — Phase D: founders now see investor
              assessments via the real shared-reports surface, not the legacy
              ephemeral investor-conclusions store, which is gone.) */}

          {/* Section Divider - Documents (collapsible header w/ count) */}
          <Pressable
            onPress={() => setDocumentsExpanded((v) => !v)}
            className="px-5 flex-row items-center"
            style={{ marginTop: 8, marginBottom: 12, gap: 12 }}
          >
            <Text
              style={{
                color: colors.text.primary,
                fontSize: 12,
                fontWeight: '700',
                letterSpacing: 1.4,
                textTransform: 'uppercase',
                flexShrink: 0,
              }}
            >
              Documents
            </Text>
            <View style={{ flex: 1, height: 1, backgroundColor: colors.border.dark }} />
            <Text
              style={{
                color: colors.text.secondary,
                fontSize: 12,
                fontWeight: '600',
              }}
              numberOfLines={1}
            >
              {documents.length}
            </Text>
            <ChevronDown
              size={14}
              color={colors.text.secondary}
              style={{ transform: [{ rotate: documentsExpanded ? '0deg' : '-90deg' }] }}
            />
          </Pressable>

          {/* Documents List (moved above Access Requests) */}
          {documentsExpanded && (
            <Animated.View
              entering={FadeInUp.delay(360).duration(500)}
              className="px-5 mb-6"
            >
              <View className="gap-3">
                {documents.map((doc) => {
                  const typeInfo = documentTypeLabels[doc.type];
                  const statusInfo = statusConfig[doc.status] ?? statusFallback;
                  const StatusIcon = statusInfo.icon;
                  const stat = viewStats?.get(doc.id);

                  return (
                    <AnimatedPressable
                      key={doc.id}
                      onPress={() => setSelectedDoc(selectedDoc?.id === doc.id ? null : doc)}
                      className="rounded-2xl overflow-hidden"
                      style={{
                        backgroundColor: colors.surface.primary,
                        ...cardShadow,
                      }}
                    >
                      <View className="p-4">
                        <View className="flex-row items-start justify-between">
                          <View className="flex-row items-start gap-3 flex-1">
                            <View
                              className="w-12 h-12 rounded-xl items-center justify-center"
                              style={{ backgroundColor: `${typeInfo.color}15` }}
                            >
                              <FileText size={24} color={typeInfo.color} />
                            </View>
                            <View className="flex-1">
                              <Text style={{ color: colors.text.primary }} className="font-medium" numberOfLines={1}>
                                {doc.name}
                              </Text>
                              <View className="flex-row items-center gap-2 mt-1.5 flex-wrap">
                                <DocumentTypeChip
                                  type={doc.type}
                                  confidence={doc.classificationConfidence}
                                  overridden={doc.typeOverridden}
                                  onChange={(next) => setDocumentType(doc.id, next)}
                                />
                                <Text style={{ color: colors.text.tertiary }} className="text-xs">{doc.size}</Text>
                                {(doc.currentVersion ?? 1) > 1 && (
                                  <View
                                    style={{
                                      paddingHorizontal: 6,
                                      paddingVertical: 1,
                                      borderRadius: 4,
                                      backgroundColor: colors.primary[50],
                                    }}
                                  >
                                    <Text
                                      style={{ color: colors.primary[700], fontSize: 10, fontWeight: '700' }}
                                    >
                                      v{doc.currentVersion}
                                    </Text>
                                  </View>
                                )}
                              </View>
                            </View>
                          </View>
                          <View
                            className="flex-row items-center gap-1 px-2 py-1 rounded-full"
                            style={{ backgroundColor: statusInfo.bg }}
                          >
                            <StatusIcon size={12} color={statusInfo.color} />
                            <Text
                              className="text-xs font-medium capitalize"
                              style={{ color: statusInfo.color }}
                            >
                              {doc.status}
                            </Text>
                          </View>
                        </View>

                        {/* Metadata Row */}
                        <View
                          className="flex-row items-center justify-between mt-3 pt-3 border-t"
                          style={{ borderColor: colors.border.light }}
                        >
                          <View className="flex-row items-center gap-4 flex-wrap">
                            {stat && stat.viewerCount > 0 && (
                              <View className="flex-row items-center gap-1">
                                <Eye size={14} color={colors.text.tertiary} />
                                <Text style={{ color: colors.text.secondary }} className="text-xs">
                                  {stat.viewerCount} viewer{stat.viewerCount === 1 ? '' : 's'}
                                </Text>
                              </View>
                            )}
                            {stat?.lastViewedAt && (
                              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                · last {timeAgo(stat.lastViewedAt)}
                              </Text>
                            )}
                            <View className="flex-row items-center gap-1">
                              <MessageSquare size={14} color={colors.text.tertiary} />
                              <Text style={{ color: colors.text.secondary }} className="text-xs">
                                {doc.comments.length} comment{doc.comments.length === 1 ? '' : 's'}
                              </Text>
                            </View>
                          </View>
                          <AnimatedPressable
                            onPress={() => handleShare(doc)}
                            className="flex-row items-center gap-1 px-3 py-1.5 rounded-full"
                            style={{ backgroundColor: `${colors.primary[600]}15` }}
                          >
                            <Share2 size={12} color={colors.primary[600]} />
                            <Text style={{ color: colors.primary[600] }} className="text-xs font-medium">Share</Text>
                          </AnimatedPressable>
                        </View>
                      </View>

                      {/* Expanded Comments */}
                      {selectedDoc?.id === doc.id && doc.comments.length > 0 && (
                        <Animated.View
                          entering={FadeIn.duration(200)}
                          className="p-4 border-t"
                          style={{ backgroundColor: colors.surface.secondary, borderColor: colors.border.light }}
                        >
                          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-3">
                            Investor Feedback
                          </Text>
                          {doc.comments.map((comment) => (
                            <View
                              key={comment.id}
                              className="rounded-xl p-3 mb-2"
                              style={{ backgroundColor: colors.surface.primary }}
                            >
                              <View className="flex-row items-center gap-2 mb-2 flex-wrap">
                                <View
                                  className="w-6 h-6 rounded-full items-center justify-center"
                                  style={{ backgroundColor: `${colors.primary[600]}15` }}
                                >
                                  <Text style={{ color: colors.primary[600] }} className="text-xs font-bold">
                                    {comment.investorName.charAt(0)}
                                  </Text>
                                </View>
                                <Text style={{ color: colors.text.primary }} className="text-sm font-medium">
                                  {comment.investorName}
                                </Text>
                                <Text style={{ color: colors.text.tertiary }} className="text-xs">{comment.date}</Text>
                                <StaleCommentTag
                                  commentedOnVersion={comment.commentedOnVersion ?? 1}
                                  currentVersion={doc.currentVersion ?? 1}
                                />
                              </View>
                              <Text style={{ color: colors.text.secondary }} className="text-sm">{comment.text}</Text>
                            </View>
                          ))}
                        </Animated.View>
                      )}
                    </AnimatedPressable>
                  );
                })}
              </View>
            </Animated.View>
          )}

          {/* Section Divider - Access Requests */}
          <View className="px-5">
            <SectionDivider label="Access Requests" marginTop={8} marginBottom={12} />
          </View>

          {/* Access Requests Section */}
          <Animated.View
            entering={FadeInUp.delay(360).duration(500)}
            className="px-5 mb-6"
          >
            <AccessRequestsSection
              requests={accessRequests}
              onApprove={(id) => updateRequestStatus(id, 'approved')}
              onReject={(id) => updateRequestStatus(id, 'rejected')}
              onRevoke={(id) => updateRequestStatus(id, 'rejected')}
            />
          </Animated.View>
        </ScrollView>

        <AILauncherFAB targetRoute="/(startup)/insights" flagKey="ai_coach" />
      </ScreenFrame>
    </View>
    {scoringConsentSheet}
    </AppLayout>
  );
}
